import {
	FC,
	FormEvent,
	ReactElement,
	ReactNode,
	useCallback,
	useContext,
	useState,
} from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Stack } from '@mui/material';
import {
	EmailAuthProvider,
	fetchSignInMethodsForEmail,
	getAuth,
	User,
} from 'firebase/auth';
import * as yup from 'yup';

import { LoginFields, SuccessStep } from '@mopla/constants';
import {
	IFormInput,
	MplButton,
	MplErrorModal,
	MplInputV2,
	PasswordInput,
	UserEmailWithEdit,
} from '@mopla/ui';
import { FormTitle } from '@mopla/ui/layouts/WelcomeScreen';
import OfflineModal from '@mopla/ui/organisms/OfflineModal/OfflineModal';
import {
	emailRequiredValidator,
	getSubmitDisabled,
	OnlineStatusContext,
} from '@mopla/utils';

import { VerifyEmailSentView } from '../RegistrationForm/VerifyEmailSentView';
import { ResendEmail } from '../ResendEmailScreen/ResendEmail';

import { TitleCompWrapper } from './styled';

interface ILoginFormInput extends IFormInput {
	serverError?: string;
}

interface IProps {
	onEmailPasswordLogin: (data: IFormInput) => void;
	processSocialSignIn?: (signInFn: () => Promise<User>) => Promise<void>;
	onPasswordless: (email: string) => void;
	authError: boolean;
	titleComp?: ReactNode;
	testId?: string;
	successStep?: SuccessStep | null;
	changeSuccessType(successTime: SuccessStep): void;
	render: (p: { content: ReactNode; submit: ReactNode }) => ReactElement;
}

type loginType = 'PW' | 'NORMAL';

export const LoginScreen: FC<IProps> = (props) => {
	const {
		onEmailPasswordLogin,
		onPasswordless,
		authError,
		titleComp = null,
		successStep,
		changeSuccessType,
		testId,
		render,
	} = props;
	const [type, setType] = useState<loginType>('PW');
	const isPWlessStep = type === 'PW';

	const hasInternetConnection = useContext(OnlineStatusContext);

	const navigate = useNavigate();
	const { t } = useTranslation(['logIn', 'validation', 'errors']);

	const methods = useForm<ILoginFormInput>({
		defaultValues: {
			[LoginFields.email]: '',
			[LoginFields.password]: '',
		},
		resolver: (data, context, options) => {
			const schema = {
				email: emailRequiredValidator(),
				password: yup.string(),
			};
			if (!isPWlessStep)
				schema.password = yup.string().required('password.required');

			return yupResolver(yup.object().shape(schema))(data, context, options);
		},
		mode: 'onTouched',
	});
	const { formState, handleSubmit, watch, clearErrors, setError } = methods;
	const { errors } = formState;
	const email = watch('email');

	const handleSubmitPWStep = () => {
		const auth = getAuth();

		fetchSignInMethodsForEmail(auth, email)
			.then((signInMethods) => {
				/** Not sure if this flow is actually working. I always get empty signInMethods */
				const isLoginWithPassword = signInMethods.includes(
					EmailAuthProvider.EMAIL_PASSWORD_SIGN_IN_METHOD
				);

				/** This one will exist for the emails already been used in the passwordless mode */
				const isPasswordlessLogin = signInMethods.includes(
					EmailAuthProvider.EMAIL_LINK_SIGN_IN_METHOD
				);

				if (!signInMethods.length || isLoginWithPassword) {
					return goToPasswordFlow();
				}

				/** Not sure if it is in use */
				if (isPasswordlessLogin) {
					return onPasswordless(email);
				}
			})
			.catch((error) => {
				console.error('Error on getting login type: ', error);
				setError('serverError', {});
			});
	};

	const goToPasswordFlow = () => setType('NORMAL');

	const getHelperText = (key: 'password') => {
		if (errors[key]) return t(`validation:${errors[key]?.message}`);
		if (authError) return t('validation:auth.error');
		return '';
	};

	const onEmailEditClick = useCallback(() => {
		setType('PW');
		clearErrors('password');
	}, [clearErrors]);

	const handleCloseAuthErrorModal = useCallback(() => {
		clearErrors('serverError');
	}, [clearErrors]);

	if (successStep === SuccessStep.pwlessLoginEmailSent) {
		const emailForSignIn = localStorage.getItem('emailForSignIn') || '';
		return (
			<VerifyEmailSentView
				email={emailForSignIn}
				onResend={() => changeSuccessType(SuccessStep.resendPwlessLoginEmail)}
				passwordless
				render={render}
			/>
		);
	}

	if (successStep === SuccessStep.resendPwlessLoginEmail) {
		const emailForSignIn = localStorage.getItem('emailForSignIn') || '';
		return (
			<ResendEmail
				onSubmit={handleSubmitPWStep}
				email={emailForSignIn}
				render={render}
			/>
		);
	}

	const handleSubmitForm = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (isPWlessStep) {
			return handleSubmitPWStep();
		}
		handleSubmit((data) => onEmailPasswordLogin(data))();
	};

	if (!hasInternetConnection) {
		return <OfflineModal />;
	}

	return render({
		content: (
			<>
				<FormProvider {...methods}>
					<form
						className="content"
						id="loginForm"
						data-testid={testId}
						onSubmit={handleSubmitForm}
					>
						{titleComp && <TitleCompWrapper>{titleComp}</TitleCompWrapper>}

						<FormTitle testId="loginForm-title">{t('label.signIn')}</FormTitle>

						<Stack gap={2}>
							{isPWlessStep && (
								<Controller
									name="email"
									render={({ field }) => (
										<MplInputV2
											{...field}
											onChange={(e) => {
												e.target.value = e.target.value.trim();
												field.onChange(e);
											}}
											label={t('label.email')}
											helperText={
												Boolean(errors.email?.message) &&
												t(`validation:${errors.email?.message}`)
											}
											error={Boolean(errors.email?.message) || authError}
											inputProps={{
												'data-testid': 'loginForm-email-textField',
											}}
											clearable
											autoFocus
											autoComplete="username email"
											type="email"
										/>
									)}
								/>
							)}
							{!isPWlessStep && (
								<UserEmailWithEdit
									email={email}
									onEditClick={onEmailEditClick}
								/>
							)}
							{!isPWlessStep && (
								<>
									<Controller
										name="password"
										render={({ field }) => (
											<PasswordInput
												{...field}
												onChange={(e) => {
													e.target.value = e.target.value.trim();
													field.onChange(e);
												}}
												label={t('label.password')}
												helperText={getHelperText('password')}
												error={Boolean(errors.password?.message || authError)}
												inputProps={{
													'data-testid': 'loginForm-password-textField',
												}}
												autoFocus
												autoComplete="current-password"
											/>
										)}
									/>
									<MplButton
										variant="text"
										type="button"
										/**
										 * Prevent the button from stealing focus so the
										 * autoFocused password field doesn't blur. On iOS the
										 * blur both triggers the onTouched "password required"
										 * validation and consumes the first tap (keyboard
										 * dismissal), swallowing the navigation click.
										 */
										onMouseDown={(e) => e.preventDefault()}
										onClick={() =>
											navigate({
												pathname: '/welcome',
												search: 'forgotPassword',
											})
										}
										testId="LoginScreen-forgotPasswordButton"
										sx={{ alignSelf: 'center' }}
									>
										{t('text.forgot')}
									</MplButton>
								</>
							)}
						</Stack>
					</form>
				</FormProvider>
				<MplErrorModal
					open={Boolean(errors.serverError)}
					testId="LoginScreen-authError-MplErrorModal"
					buttons={
						<MplButton
							fullWidth
							color="primaryButtonV2"
							onClick={handleCloseAuthErrorModal}
						>
							{t('errors:button.close')}
						</MplButton>
					}
				/>
			</>
		),
		submit: (
			<MplButton
				fullWidth
				color="primaryButtonV2"
				data-testid="loginForm-login-button"
				disabled={getSubmitDisabled(methods)}
				type="submit"
				form="loginForm"
			>
				{t(`button.${isPWlessStep ? 'continue' : 'logIn'}`)}
			</MplButton>
		),
	});
};
